import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { ACMeetingSettingsDialog } from './ACMeetingSettingsDialog';
import { ACPostMeetingDialog } from './ACPostMeetingDialog';
import { ACEmailPreviewDialog } from './ACEmailPreviewDialog';
import { Settings, Landmark, CheckCircle, ThumbsUp, HelpCircle, ThumbsDown, MessageSquareText, Clock, ListPlus, Loader2, AudioLines, Plus, Trash2, BarChart3, Mail, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const ACMeetingCard = ({ isAdmin, navigate, onAgendaChanged, onSummaryArchived }) => {
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addingQuestions, setAddingQuestions] = useState(false);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [postMeeting, setPostMeeting] = useState(null);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loggingAdhoc, setLoggingAdhoc] = useState(false);
  const [updNotes, setUpdNotes] = useState({});
  const [savedNotes, setSavedNotes] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getAcStatus();
      setStatus(res.data);
      const n = {};
      (res.data.deals || []).forEach(d => { n[d.project_id] = d.ac_update_note || ''; });
      setUpdNotes(n);
      setSavedNotes(n);
    } catch {}
    try { const res = await api.getAcFeedbackOverview(); setOverview(res.data); } catch {}
    try { const res = await api.getAcStats(); setStats(res.data); } catch {}
    try { const res = await api.getAcAgendaItems(); setItems(res.data); } catch {}
    try { const res = await api.getAcPostMeeting(); setPostMeeting(res.data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAddQuestionDeals = async () => {
    setAddingQuestions(true);
    try {
      const res = await api.addAcQuestionDeals();
      const { added, total_questions, limit_reached, bucket } = res.data;
      if (added === 0) {
        toast.info(total_questions === 0 ? 'No deals with "Questions" votes.' : 'All "Questions" deals are already on an agenda.');
      } else {
        toast.success(`${added} deal${added !== 1 ? 's' : ''} added to the ${bucket === 'next' ? 'following meeting' : 'team meeting'} agenda`);
      }
      if (limit_reached) toast.warning('Agenda limit reached — not all deals were added.');
      onAgendaChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add deals'); }
    setAddingQuestions(false);
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    setAddingItem(true);
    try {
      await api.addAcAgendaItem(newItemTitle.trim());
      setNewItemTitle('');
      const res = await api.getAcAgendaItems();
      setItems(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add the agenda item'); }
    setAddingItem(false);
  };

  const handleDeleteItem = async (id) => {
    try {
      await api.deleteAcAgendaItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const moveItem = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.reorderAcAgendaItems(arr.map(i => i.id)); }
    catch { toast.error('Reihenfolge konnte nicht gespeichert werden'); load(); }
  };

  const saveUpdateNote = async (pid) => {
    setSavingNote(pid);
    try {
      await api.setAcUpdateNote(pid, updNotes[pid] || '');
      setSavedNotes(s => ({ ...s, [pid]: updNotes[pid] || '' }));
      toast.success('Update saved — will appear in the AC email');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save the update'); }
    setSavingNote(null);
  };

  const toggleDiscussed = async (d) => {
    const next = !d.previously_discussed;
    try {
      await api.setAcDiscussed(d.project_id, next);
      toast.success(next ? 'Marked as discussed — compact recap block in the AC email' : 'Marked as NOT discussed — the AC email shows the full deal block again');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleLogAdhoc = async () => {
    setLoggingAdhoc(true);
    try {
      const res = await api.logAdhocAcMeeting();
      if (res.data.existing) toast.info('An AC meeting for today is already logged — opening it.');
      else toast.success('AC meeting logged — current flagged deals & agenda items attached.');
      await load();
      setPostOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to log the AC meeting'); }
    setLoggingAdhoc(false);
  };

  if (!status) return null;
  if (!status.enabled && !status.next_meeting_date && !isAdmin) return null;

  const overflow = Math.max(0, status.total_flagged - status.deals.length);
  const questionDeals = overview?.deals?.filter(d => d.feedback.some(f => f.vote === 'questions')).length || 0;

  return (
    <div className="bg-card border border-purple-500/25 rounded-sm" data-testid="ac-meeting-card">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Landmark className="w-4 h-4 text-purple-400" />
              <p className="font-heading text-base text-foreground">AC Meeting</p>
              {status.next_meeting_date ? (
                <span className="text-xs text-purple-300 font-medium" data-testid="ac-next-date">
                  {fmtDate(status.next_meeting_date)}
                  {status.days_until_meeting != null && status.days_until_meeting >= 0 && (
                    <span className="text-muted-foreground"> — in {status.days_until_meeting} day{status.days_until_meeting !== 1 ? 's' : ''}</span>
                  )}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground italic">no date set</span>
              )}
              {!status.enabled && <span className="text-[9px] font-mono text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded px-1.5 py-0.5">AUTO-EMAIL OFF</span>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="ac-email-status">
              {status.email_sent ? (
                <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Agenda email sent for this meeting.</span>
              ) : status.email_date ? (
                <>Agenda email to the AC ({status.recipients_count} recipient{status.recipients_count !== 1 ? 's' : ''}) goes out on <strong className="text-foreground">{fmtDate(status.email_date)}</strong> — 3 workdays of review time. Bi-weekly rhythm, max {status.max_deals} deals (FIFO).</>
              ) : (
                <>Bi-weekly AC meeting — '!'-flagged deals (max {status.max_deals}, oldest first) are emailed 3 workdays before.</>
              )}
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={() => setPreviewOpen(true)} title="Exakte Vorschau der AC-Agenda-E-Mail (nichts wird versendet)" data-testid="ac-email-preview-btn"
                className="h-8 px-2 text-purple-300 border border-purple-500/30 hover:bg-purple-500/10 rounded-sm transition-colors flex items-center gap-1.5 text-xs">
                <Mail className="w-3.5 h-3.5" /> E-Mail-Vorschau
              </button>
              <button onClick={() => setSettingsOpen(true)} title="AC meeting settings" data-testid="ac-settings-btn"
                className="h-8 px-2 text-muted-foreground hover:text-foreground border border-border rounded-sm transition-colors flex items-center gap-1.5 text-xs">
                <Settings className="w-3.5 h-3.5" /> AC Settings
              </button>
            </div>
          )}
        </div>
      </div>
      {stats && (
        <div className="px-4 py-2.5 border-b border-border bg-secondary/20" data-testid="ac-stats-block">
          <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5 mb-1.5">
            <BarChart3 className="w-3 h-3" /> Fixed agenda block — Pipeline & Outreach (included in every AC email)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {stats.pipeline.filter(p => p.count > 0).map(p => (
              <span key={p.stage} className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">
                {p.label} <strong className="text-purple-300">{p.count}</strong>
              </span>
            ))}
            <span className="text-[10px] font-mono text-purple-300 border border-purple-500/30 bg-purple-500/10 rounded px-1.5 py-0.5">
              Total active <strong>{stats.total_active}</strong>
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <span className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">Letters in progress <strong className="text-sky-300">{stats.outreach.letters_in_progress}</strong></span>
            <span className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">Letters sent <strong className="text-sky-300">{stats.outreach.letters_sent}</strong></span>
            <span className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">Responses <strong className="text-emerald-300">{stats.outreach.letters_responded}</strong></span>
            <span className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">Active sequences <strong className="text-sky-300">{stats.outreach.sequences_active}</strong></span>
            <span className="text-[10px] font-mono text-foreground bg-secondary border border-border rounded px-1.5 py-0.5">Active campaigns <strong className="text-sky-300">{stats.outreach.campaigns_active}</strong></span>
          </div>
          {stats.outreach.contacted_total > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="ac-stats-cumulative">
              Kumulativ kontaktiert: <strong className="text-foreground">{stats.outreach.contacted_total}</strong> Deals
              — {stats.outreach.contacted_now} aktuell in Contacted · {stats.outreach.contacted_advanced} weitergezogen · {stats.outreach.contacted_passed} nach Kontakt gepasst.
              {stats.outreach.prop_passed_total > 0 && <> Insgesamt gepasst (inkl. Screening): <strong className="text-foreground">{stats.outreach.prop_passed_total}</strong>.</>}
              {' '}Der Funnel zeigt den aktuellen Stand je Stage (nicht kumulativ).
            </p>
          )}
        </div>
      )}
      {status.deals.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-5">No '!'-flagged deals queued for the AC meeting.</p>
      ) : (
        <>
          {status.deals.map((d, i) => (
            <div key={d.project_id} className="px-4 py-2.5 border-b border-border last:border-b-0"
              data-testid={`ac-deal-${d.project_id}`}>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono text-muted-foreground w-5 text-right shrink-0">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => navigate(`/projects/${d.project_id}`)}
                      className="text-sm font-medium text-foreground hover:text-purple-400 transition-colors truncate">
                      {d.project_name}
                    </button>
                    <span className="text-[9px] font-mono text-muted-foreground bg-secondary border border-border rounded px-1.5 py-0.5">
                      {d.stage_label}
                    </span>
                    {d.previously_discussed && (
                      <button type="button" onClick={() => toggleDiscussed(d)}
                        className="text-[9px] font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 rounded px-1.5 py-0.5 transition-colors"
                        title="Counts as discussed in the last AC meeting — the email shows a compact recap block. Click if it was NOT actually discussed."
                        data-testid={`ac-rediscussed-badge-${d.project_id}`}>
                        Previously discussed{d.last_ac_date ? ` · ${new Date(d.last_ac_date + 'T00:00:00').toLocaleDateString('de-DE')}` : ''}
                      </button>
                    )}
                    {d.previously_on_agenda && (
                      <button type="button" onClick={() => toggleDiscussed(d)}
                        className="text-[9px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 rounded px-1.5 py-0.5 transition-colors"
                        title="Was on the AC agenda but not discussed — the email shows the full details. Click if it WAS discussed after all."
                        data-testid={`ac-on-agenda-badge-${d.project_id}`}>
                        On agenda, not discussed{d.last_ac_date ? ` · ${new Date(d.last_ac_date + 'T00:00:00').toLocaleDateString('de-DE')}` : ''}
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Flagged by {d.added_by || '—'}{d.flagged_at ? ` · ${new Date(d.flagged_at).toLocaleDateString('de-DE')}` : ''}
                  </p>
                </div>
              </div>
              {d.previously_discussed && (
                <div className="ml-8 mt-1.5" data-testid={`ac-update-note-box-${d.project_id}`}>
                  <textarea value={updNotes[d.project_id] ?? ''}
                    onChange={e => setUpdNotes(n => ({ ...n, [d.project_id]: e.target.value }))}
                    rows={2}
                    placeholder="Updates since the last AC meeting — goes into the compact email block…"
                    data-testid={`ac-update-note-input-${d.project_id}`}
                    className="w-full bg-secondary border border-border rounded-sm px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:border-blue-500 resize-y" />
                  {(updNotes[d.project_id] ?? '') !== (savedNotes[d.project_id] ?? '') && (
                    <button onClick={() => saveUpdateNote(d.project_id)} disabled={savingNote === d.project_id}
                      data-testid={`ac-update-note-save-${d.project_id}`}
                      className="mt-1 h-6 px-2 text-[10px] text-blue-300 border border-blue-500/30 hover:bg-blue-500/10 rounded-sm transition-colors flex items-center gap-1 disabled:opacity-50">
                      {savingNote === d.project_id && <Loader2 className="w-3 h-3 animate-spin" />} Save update
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {overflow > 0 && (
            <p className="text-[11px] text-amber-400 px-4 py-2 border-t border-border" data-testid="ac-overflow-hint">
              +{overflow} more flagged deal{overflow !== 1 ? 's' : ''} — will move up for the following AC meeting (FIFO).
            </p>
          )}
        </>
      )}
      <div className="border-t border-border px-4 py-2.5" data-testid="ac-agenda-items-section">
        <p className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1">AC agenda items — go into the next AC email &amp; the post-meeting Plaud mapping</p>
        {items.map((it, idx) => (
          <div key={it.id} className="flex items-center gap-2 py-1 group" data-testid={`ac-agenda-item-${it.id}`}>
            <ListPlus className="w-3 h-3 text-purple-400/60 shrink-0" />
            <span className="text-xs text-foreground flex-1 min-w-0 truncate">{it.title}</span>
            <span className="text-[10px] text-muted-foreground shrink-0">{it.added_by}</span>
            <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} data-testid={`ac-agenda-item-up-${it.id}`}
              title="Nach oben"
              className="text-muted-foreground/30 hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-0 disabled:pointer-events-none">
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} data-testid={`ac-agenda-item-down-${it.id}`}
              title="Nach unten"
              className="text-muted-foreground/30 hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-0 disabled:pointer-events-none">
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDeleteItem(it.id)} data-testid={`ac-agenda-item-delete-${it.id}`}
              className="text-muted-foreground/30 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-1.5">
          <input value={newItemTitle} onChange={e => setNewItemTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddItem(); }}
            placeholder="Add agenda item (e.g. Fund update, Q&A)..."
            className="flex-1 h-8 bg-secondary border border-border rounded-sm px-2.5 text-xs text-foreground focus:outline-none focus:border-purple-500"
            data-testid="ac-agenda-item-input" />
          <button onClick={handleAddItem} disabled={addingItem || !newItemTitle.trim()} data-testid="ac-agenda-item-add-btn"
            className="h-8 px-2.5 text-xs text-purple-300 border border-purple-500/30 hover:bg-purple-500/10 rounded-sm transition-colors flex items-center gap-1 disabled:opacity-40">
            {addingItem ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
          </button>
        </div>
      </div>
      {postMeeting && (
        <div className="border-t border-border px-4 py-3" data-testid="ac-post-meeting-section">
          {postMeeting.meeting ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <AudioLines className="w-3.5 h-3.5 text-violet-400" />
                  Post-Meeting — AC Meeting {fmtDate(postMeeting.meeting.meeting_date)}
                </p>
                {postMeeting.meeting.post_summary ? (
                  <p className="text-[11px] text-emerald-400 mt-0.5 flex items-center gap-1" data-testid="ac-post-summary-sent">
                    <CheckCircle className="w-3 h-3 shrink-0" />
                    Summary sent {new Date(postMeeting.meeting.post_summary.sent_at).toLocaleDateString('de-DE')} to {postMeeting.meeting.post_summary.sent_to.length} recipient{postMeeting.meeting.post_summary.sent_to.length !== 1 ? 's' : ''} — archived under Meeting Summaries.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground mt-0.5">Upload the Plaud notes, review the AI summary per deal and email it to the present AC members + team.</p>
                )}
                <button onClick={handleLogAdhoc} disabled={loggingAdhoc} data-testid="ac-log-adhoc-btn"
                  className="text-[11px] text-purple-300 hover:text-purple-200 mt-1 flex items-center gap-1 disabled:opacity-50">
                  {loggingAdhoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Log a new AC meeting (today, no agenda email needed)
                </button>
              </div>
              <button onClick={() => setPostOpen(true)} data-testid="ac-post-meeting-btn"
                className="h-8 px-3 text-xs text-violet-300 border border-violet-500/30 hover:bg-violet-500/10 rounded-sm transition-colors flex items-center gap-1.5 shrink-0">
                <AudioLines className="w-3.5 h-3.5" /> {postMeeting.meeting.post_summary ? 'Review & send again' : 'Plaud & Summary'}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <AudioLines className="w-3.5 h-3.5 text-violet-400" /> Post-Meeting
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">No AC meeting on record yet — log one to start the Plaud post-meeting workflow (works without a prior agenda email).</p>
              </div>
              <button onClick={handleLogAdhoc} disabled={loggingAdhoc} data-testid="ac-log-adhoc-btn"
                className="h-8 px-3 text-xs text-violet-300 border border-violet-500/30 hover:bg-violet-500/10 rounded-sm transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                {loggingAdhoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Log AC meeting (today)
              </button>
            </div>
          )}
        </div>
      )}
      {overview?.deals?.length > 0 && (
        <div className="border-t border-purple-500/25" data-testid="ac-feedback-overview">
          <div className="px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <MessageSquareText className="w-3.5 h-3.5 text-purple-400" />
              <p className="text-xs font-medium text-foreground">
                AC Feedback — Meeting {fmtDate(overview.meeting.meeting_date)}
              </p>
            </div>
            {questionDeals > 0 && (
              <button onClick={handleAddQuestionDeals} disabled={addingQuestions} data-testid="ac-add-questions-btn"
                className="h-7 px-2 text-[11px] text-blue-300 border border-blue-500/30 hover:bg-blue-500/10 rounded-sm transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                {addingQuestions ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListPlus className="w-3 h-3" />}
                "Questions" → Team Agenda ({questionDeals})
              </button>
            )}
          </div>
          {overview.deals.map(d => (
            <div key={d.project_id} className="px-4 py-2 border-t border-border/50" data-testid={`ac-feedback-deal-${d.project_id}`}>
              <button onClick={() => navigate(`/projects/${d.project_id}`)}
                className="text-xs font-medium text-foreground hover:text-purple-400 transition-colors">
                {d.project_name}
              </button>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {d.feedback.map(f => {
                  const meta = {
                    interested: { Icon: ThumbsUp, cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
                    questions: { Icon: HelpCircle, cls: 'text-blue-300 border-blue-500/40 bg-blue-500/10' },
                    decline: { Icon: ThumbsDown, cls: 'text-red-300 border-red-500/40 bg-red-500/10' },
                  }[f.vote] || { Icon: MessageSquareText, cls: 'text-muted-foreground border-border bg-secondary' };
                  return (
                    <span key={f.email} title={f.comment || ''}
                      className={`inline-flex items-center gap-1 text-[10px] font-mono border rounded px-1.5 py-0.5 ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {f.email.split('@')[0]}
                      {f.comment && <MessageSquareText className="w-2.5 h-2.5 opacity-60" />}
                    </span>
                  );
                })}
                {d.missing.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5"
                    title={d.missing.join(', ')}>
                    <Clock className="w-3 h-3" /> {d.missing.length} pending
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ACMeetingSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />
      <ACEmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} onSent={load} />
      <ACPostMeetingDialog open={postOpen} onOpenChange={setPostOpen} meeting={postMeeting?.meeting}
        teamRecipients={postMeeting?.team_recipients || []} onSent={() => { load(); onSummaryArchived?.(); }} />
    </div>
  );
};
